import { TemplateElement, html, defineElement, when } from '@webtides/element-js';
import i18n from '../../../../utils/i18n.js';
import '../../../icon/icon.js';
import style from './author-teaser.css';

/**
 * @property {'default' || 'profile'} variant
 * @property {string} name
 * @property {string} description
 * @property {ImageProperties} image
 * @property {string} pageLink
 * @property {string} role
 * @property {string} linkedInPageLink
 */
export default class AuthorTeaser extends TemplateElement {
    constructor() {
        super({ styles: [style] });
    }

    properties() {
        return {
            variant: 'default',
            name: '',
            description: '',
            image: null,
            pageLink: '',
            role: '',
            linkedInPageLink: '',
        };
    }

    get isCurrentPage() {
        const urlWithoutOrigin = window.location.href.replace(window.location.origin, '');
        return urlWithoutOrigin === this.pageLink;
    }

    template() {
        if (this.variant === 'profile') {
            return html`
                <div class="w-full lg:w-10/12 lg:mx-auto flex flex-col items-center justify-center gap-16">
                    <img
                        loading="lazy"
                        src="${this.image.src}"
                        srcset="${this.image.srcset}"
                        sizes="(min-width: 1024px) 200px, 80px"
                        alt="${this.image.alt}"
                        class="rounded-full object-cover size-80 aspect-square lg:size-[200px]"
                    />
                    <h1 class="mb-0 font-medium leading-[130%] text-[24px] lg:text-[32px] text-green-medium">
                        ${this.name}
                    </h1>
                    <p class="text-center mb-0 fix-body2">${this.description}</p>
                    ${when(
                        this.linkedInPageLink,
                        html`
                            <a
                                href="${this.linkedInPageLink}"
                                class="rounded-full bg-green-medium text-[white] size-24 lg:size-48 flex items-center justify-center"
                                element="author teaser"
                            >
                                <mb-icon name="social--linkedin" size="10" class="lg:hidden"></mb-icon>
                                <mb-icon name="social--linkedin" size="24" class="hidden lg:flex"></mb-icon>
                            </a>
                        `,
                    )}
                </div>
            `;
        }

        return html`
            <div
                class="flex w-full items-center bg-green-light25 rounded-[var(--mdx-sys-border-radius-default)] py-fix-s px-fix-m gap-fix-s relative hover:bg-[#DCE4E2] "
            >
                <img
                    loading="lazy"
                    src="${this.image.src}"
                    srcset="${this.image.srcset}"
                    sizes="48px"
                    alt="${this.image.alt}"
                    class="size-48 rounded-full"
                />
                <div>
                    <div class="flex flex-col items-stretch justify-between">
                        <div class="flex flex-row gap-8 items-center">
                            <h3 class="mb-0 fix-title3-medium @md:fix-body2-medium @lg:fix-title3-medium">
                                <a href="${this.pageLink}" class="stretched-link @sm:hidden text-lg">${this.name}</a>
                                <span class="hidden @sm:inline text-lg">${this.name}</span>
                            </h3>
                            ${when(
                                this.linkedInPageLink,
                                html`
                                    <a
                                        href="${this.linkedInPageLink}"
                                        class="text-green-light25 rounded-full bg-primary p-2 h-24 w-24 flex items-center justify-center"
                                        element="author teaser"
                                    >
                                        <mb-icon class="h-12 w-12" name="social--linkedin"></mb-icon>
                                    </a>
                                `,
                            )}
                        </div>
                        ${when(this.role, html`<div class="@sm:mb-8 text-base">${this.role}</div>`)}
                        <p class="hidden @sm:block text-base text-green-light mb-0">${this.description}</p>
                    </div>
                </div>
                ${when(
                    !this.isCurrentPage,
                    html`<div class="ml-auto hidden @sm:block">
                        <a
                            href="${this.pageLink}"
                            class="block w-max border-[1px] p-8 rounded-full border-green-dark "
                            element="author teaser"
                        >
                            <mb-icon name="app--chevron-right" class="h-24 w-24"></mb-icon
                        ></a>
                    </div> `,
                )}
            </div>
        `;
    }
}

defineElement('mb-author-teaser', AuthorTeaser);
